import { z } from "zod";

/** Section ids for the working-time and project-time permission matrices. */
export const WORKINGHOURS_MATRIX_ID = "ws-wh-perms";
export const PROJECTHOURS_MATRIX_ID = "ws-ph-perms";

export const permissionMatrixSearchSchema = z.object({
	group: z.string().min(1).optional().catch(undefined),
});

export type PermissionMatrixSearch = z.infer<typeof permissionMatrixSearchSchema>;

/** Group column id from workspace hours route search (`?group=`). */
export function permissionMatrixGroupFromSearch(search: unknown): string | undefined {
	if (typeof search !== "object" || search === null || !("group" in search)) {
		return undefined;
	}
	const value = search.group;
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

export interface PermissionMatrixLink {
	readonly hash: typeof PROJECTHOURS_MATRIX_ID | typeof WORKINGHOURS_MATRIX_ID;
	readonly to: "/settings/workspace/projecthours" | "/settings/workspace/workinghours";
}

/** Matrix tab that is actually available for this workspace. */
export function permissionMatrixLink(workspace: {
	readonly projecthoursEnabled: boolean;
	readonly workinghoursEnabled: boolean;
}): PermissionMatrixLink {
	if (workspace.projecthoursEnabled && !workspace.workinghoursEnabled) {
		return { to: "/settings/workspace/projecthours", hash: PROJECTHOURS_MATRIX_ID };
	}
	return { to: "/settings/workspace/workinghours", hash: WORKINGHOURS_MATRIX_ID };
}

/**
 * `undefined` leaves the matrix alone.
 * `null` scrolls the matrix into view.
 * A group id also highlights that column when it is rendered.
 */
export function permissionMatrixFocus(opts: {
	readonly group?: string;
	readonly hash: string;
}): string | null | undefined {
	const hash = opts.hash.startsWith("#") ? opts.hash.slice(1) : opts.hash;
	const targetsMatrix = hash === WORKINGHOURS_MATRIX_ID || hash === PROJECTHOURS_MATRIX_ID;
	if (!(targetsMatrix || opts.group)) {
		return undefined;
	}
	return opts.group ?? null;
}
